import { type ReactElement } from 'react';
import { getChartTitle } from '@/shared/copy/charts';
import { isLightTheme } from '@/theme/semanticTokens';
import { s } from '@/theme/tokens';
import { Stack } from '@/ui';
import { CheckboxPopover } from '@/ui/composites/filters/CheckboxPopover';
import { Box, Text, useTheme } from '@/ui/primitives';
import { useMediaQuery } from '@/ui/primitives/hooks';
import { TABLE_PANEL_HEADER_HEIGHT } from './tableConstants';
import { TableSortPopover } from './TableSortPopover';
import { buildToolbarIconButtonSx } from './tableToolbar.helpers';
import { type TableToolbarProps } from './tableToolbar.types';
import { TableToolbarActions } from './TableToolbarActions';
import { useTableToolbarState } from './useTableToolbarState';
import type { DateQuickFilter } from '@/features/shared/hooks/useDateWindow';

export const TableToolbar = <TSortKey extends string, TStatus extends string>({
  title,
  searchAriaLabel: _searchAriaLabel,
  searchValue,
  onDebouncedSearchChange,
  columnVisibilityModel,
  defaultColumnVisibilityModel,
  columnOptions,
  onColumnVisibilityModelChange,
  sortKey,
  sortDirection,
  defaultSortKey,
  defaultSortDirection,
  sortOptions,
  quickSortOptions = [],
  onSortChange,
  filterStatuses,
  statusOptions: _statusOptions,
  onFilterStatusesChange,
  filterEntities = [],
  onFilterEntitiesChange,
  entityOptions: _entityOptions,
  entityLabel: _entityLabel,
  filterItems = [],
  onFilterItemsChange,
  itemOptions: _itemOptions,
  itemLabel: _itemLabel,
  filterSuites = [],
  onFilterSuitesChange,
  suiteOptions: _suiteOptions,
  filterTags,
  onFilterTagsChange,
  dateQuickFilter,
  onDateQuickFilterChange,
  fromDate,
  toDate,
  latestQueryStartDay = null,
  latestQueryEndDay = null,
  previewFromDate = null,
  previewToDate = null,
  onFromDateChange,
  onToDateChange,
  availableTags: _availableTags,
  includeLatestQuickFilter: _includeLatestQuickFilter = false,
  hideDateRangeLabel = false,
  showControlsWithoutSummary = false,
  statusFilterLabel: _statusFilterLabel = getChartTitle('latestStatus'),
  summaryCount = null,
  summarySingularLabel,
  summaryPluralLabel,
}: TableToolbarProps<TSortKey, TStatus>): ReactElement => {
  const mandatoryColumnVisibilityModel = columnOptions.reduce<Record<string, true>>(
    (acc, option) => {
      if (option.mandatory) {
        acc[option.field] = true;
      }
      return acc;
    },
    {},
  );
  const theme = useTheme();
  const hideNarrowToolbarDateLabel = useMediaQuery(theme.breakpoints.down('sm'));
  const toolbarControlSize = 32;
  const toolbarInactiveIconColor = isLightTheme(theme) ? 'text.secondary' : 'common.white';
  const {
    popoverState,
    hoverTarget,
    hasNonDefaultColumns,
    hasActiveSort,
    dateRangeLabel,
    setHoverTarget,
    openPopover,
    closePopover,
    preserveWindowScroll,
  } = useTableToolbarState({
    searchValue,
    onDebouncedSearchChange,
    columnVisibilityModel,
    defaultColumnVisibilityModel,
    columnOptions,
    onColumnVisibilityModelChange,
    sortKey,
    sortDirection,
    defaultSortKey,
    defaultSortDirection,
    onSortChange,
    filterStatuses,
    onFilterStatusesChange,
    filterEntities,
    onFilterEntitiesChange,
    filterItems,
    onFilterItemsChange,
    filterSuites,
    onFilterSuitesChange,
    filterTags,
    onFilterTagsChange,
    dateQuickFilter,
    onDateQuickFilterChange,
    fromDate,
    toDate,
    latestQueryStartDay,
    latestQueryEndDay,
    onFromDateChange,
    onToDateChange,
    summaryCount,
    summarySingularLabel,
    summaryPluralLabel,
    previewFromDate,
    previewToDate,
  });
  const toolbarIconButtonSx = buildToolbarIconButtonSx(
    toolbarInactiveIconColor,
    toolbarControlSize,
  );
  const hasActiveTableToolbarState = hasNonDefaultColumns || hasActiveSort;
  const visibleDateRangeLabel =
    hideDateRangeLabel || hideNarrowToolbarDateLabel ? null : dateRangeLabel;

  if (
    !title &&
    !visibleDateRangeLabel &&
    !hasActiveTableToolbarState &&
    !showControlsWithoutSummary
  ) {
    return <></>;
  }

  const resetTableToolbar = (): void => {
    preserveWindowScroll(() => {
      onSortChange(defaultSortKey, defaultSortDirection);
      onColumnVisibilityModelChange({
        ...defaultColumnVisibilityModel,
        ...mandatoryColumnVisibilityModel,
      });
      closePopover();
    });
  };
  const handleQuickSort = (nextSortKey: TSortKey, firstDirection: 'asc' | 'desc'): void => {
    preserveWindowScroll(() => {
      if (sortKey !== nextSortKey) {
        onSortChange(nextSortKey, firstDirection);
        return;
      }
      if (sortDirection === firstDirection) {
        onSortChange(nextSortKey, firstDirection === 'asc' ? 'desc' : 'asc');
        return;
      }
      onSortChange(defaultSortKey, defaultSortDirection);
    });
  };

  return (
    <Stack
      direction="row"
      justifyContent={title || visibleDateRangeLabel ? 'space-between' : 'flex-end'}
      alignItems="center"
      gap="md"
      sx={{
        width: '100%',
        minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
        height: `${TABLE_PANEL_HEADER_HEIGHT}px`,
      }}
    >
      {title || visibleDateRangeLabel ? (
        <Box
          sx={{
            minHeight: `${toolbarControlSize}px`,
            display: 'flex',
            alignItems: 'center',
            pl: s('sm'),
            minWidth: 0,
            flex: '1 1 auto',
          }}
        >
          {title ? (
            <Text
              role="bodyStrong"
              sx={{
                fontSize: (muiTheme) => muiTheme.typography.h6.fontSize,
                lineHeight: (muiTheme) => muiTheme.typography.h6.lineHeight,
              }}
            >
              {title}
            </Text>
          ) : visibleDateRangeLabel ? (
            <Text
              role="body"
              sx={{
                color: 'text.secondary',
                fontSize: (muiTheme) => muiTheme.typography.pxToRem(15),
                lineHeight: 1.4,
                whiteSpace: 'nowrap',
              }}
            >
              {visibleDateRangeLabel}
            </Text>
          ) : null}
        </Box>
      ) : null}
      <Stack
        direction="row"
        alignItems="center"
        gap="sm"
        sx={{
          minWidth: 0,
          flexShrink: 0,
          height: `${TABLE_PANEL_HEADER_HEIGHT}px`,
          minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
          boxSizing: 'border-box',
          justifyContent: 'center',
        }}
      >
        <TableToolbarActions
          hasActiveSort={hasActiveSort}
          hasActiveTableToolbarState={hasActiveTableToolbarState}
          hasNonDefaultColumns={hasNonDefaultColumns}
          hoverTarget={hoverTarget}
          onColumnsOpen={openPopover('columns')}
          onQuickSort={handleQuickSort}
          onReset={resetTableToolbar}
          onSetHoverTarget={setHoverTarget}
          onSortOpen={openPopover('sort')}
          quickSortOptions={quickSortOptions}
          sortDirection={sortDirection}
          sortKey={sortKey}
          toolbarControlSize={toolbarControlSize}
          toolbarIconButtonSx={toolbarIconButtonSx}
          toolbarInactiveIconColor={toolbarInactiveIconColor}
        />
      </Stack>
      <CheckboxPopover
        open={popoverState.type === 'columns'}
        anchorEl={popoverState.anchorEl}
        onClose={closePopover}
        minWidth={300}
        sections={[
          {
            key: 'columns',
            options: columnOptions.map((option) => {
              const checked = option.mandatory ? true : columnVisibilityModel[option.field];
              return {
                key: option.field,
                label: option.label,
                checked,
                disabled: Boolean(option.mandatory),
                onChange: (nextChecked: boolean) => {
                  if (option.mandatory) return;
                  preserveWindowScroll(() => {
                    onColumnVisibilityModelChange({
                      ...columnVisibilityModel,
                      [option.field]: nextChecked,
                      ...mandatoryColumnVisibilityModel,
                    });
                  });
                },
              };
            }),
          },
        ]}
      />
      <TableSortPopover
        open={popoverState.type === 'sort'}
        anchorEl={popoverState.anchorEl}
        onClose={closePopover}
        sortKey={sortKey}
        sortDirection={sortDirection}
        sortOptions={sortOptions}
        onSortChange={onSortChange}
        preserveWindowScroll={preserveWindowScroll}
      />
    </Stack>
  );
};

export type { DateQuickFilter };
